import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { EmojiPicker } from '../components/EmojiPicker'
import { ErrorText, MIN_PASSWORD_LENGTH, PasswordHint } from '../components/ui'

interface DraftMember {
  name: string
  role: 'parent' | 'kid'
  emoji: string
}

// Default picks handed out as people are added, so new rows don't all look the same.
const STARTER_EMOJIS = ['🙂', '😎', '🦊', '🐙', '🐝', '🌻', '🚀', '🎧', '⚽️', '🎸', '🐈', '🐕']

const detectedTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  } catch {
    return 'America/Los_Angeles'
  }
}

export function SignUp() {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [householdName, setHouseholdName] = useState('')
  const [timezone, setTimezone] = useState(detectedTimezone)
  const [pin, setPin] = useState('')
  const [meIndex, setMeIndex] = useState(0)
  const [members, setMembers] = useState<DraftMember[]>([
    { name: '', role: 'parent', emoji: '🙂' },
    { name: '', role: 'kid', emoji: '😎' },
  ])

  const named = members.filter((member) => member.name.trim().length > 0)

  const create = useMutation({
    mutationFn: () => {
      const cleaned = members
        .map((member, index) => ({ member, index }))
        .filter(({ member }) => member.name.trim().length > 0)

      // meIndex refers to the list the parent sees; remap it onto the list
      // actually sent, since blank rows are dropped.
      const remappedMe = cleaned.findIndex(({ index }) => index === meIndex)

      return api.signUp({
        email: email.trim(),
        password,
        householdName: householdName.trim(),
        timezone,
        pin,
        members: cleaned.map(({ member }) => ({ ...member, name: member.name.trim() })),
        meIndex: remappedMe,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries()
    },
  })

  const update = (index: number, patch: Partial<DraftMember>): void => {
    setMembers((current) => current.map((member, i) => (i === index ? { ...member, ...patch } : member)))
  }

  const accountReady = email.trim().length > 3 && password.length >= MIN_PASSWORD_LENGTH && householdName.trim().length > 0
  const meIsParent = members[meIndex]?.role === 'parent' && (members[meIndex]?.name.trim().length ?? 0) > 0
  const canSubmit = accountReady && /^\d{4,8}$/.test(pin) && named.length > 0 && meIsParent && !create.isPending

  if (step === 1) {
    return (
      <div className="page" style={{ paddingTop: 'var(--space-6)', minHeight: '100dvh' }}>
        <div className="stack">
          <h1 className="title" style={{ fontSize: '1.8rem' }}>
            Set up your household
          </h1>
          <p className="subtitle">First, an account for you. You'll add everyone else next.</p>
        </div>

        <section className="card">
          <div className="field">
            <label className="label" htmlFor="su-household">
              What should we call your household?
            </label>
            <input
              id="su-household"
              className="input"
              value={householdName}
              onChange={(event) => setHouseholdName(event.target.value)}
              placeholder="The Morrisons"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="su-email">
              Your email
            </label>
            <input
              id="su-email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <span className="faint">Used to sign in and to recover the household if you forget.</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="su-password">
              Password
            </label>
            <input
              id="su-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <PasswordHint password={password} />
          </div>

          <div className="field">
            <label className="label" htmlFor="su-tz">
              Timezone
            </label>
            <input
              id="su-tz"
              className="input"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            />
            <span className="faint">
              Days and weeks reset on this clock — not on each phone's clock.
            </span>
          </div>
        </section>

        <button
          type="button"
          className="btn btn-primary btn-block btn-lg"
          disabled={!accountReady}
          onClick={() => setStep(2)}
        >
          Next
        </button>
        <Link to="/" className="btn btn-quiet btn-block">
          Back
        </Link>
      </div>
    )
  }

  return (
    <div className="page" style={{ paddingTop: 'var(--space-6)' }}>
      <div className="stack">
        <h1 className="title" style={{ fontSize: '1.8rem' }}>
          Who lives here?
        </h1>
        <p className="subtitle">You can change any of this later in Settings.</p>
      </div>

      <section className="card">
        <div className="field">
          <label className="label" htmlFor="su-pin">
            Household PIN
          </label>
          <input
            id="su-pin"
            className="input tabular"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            value={pin}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="4 to 8 digits"
          />
          <span className="faint">
            Another parent enters this once, with an invite code, to set up their own login.
          </span>
        </div>
      </section>

      <section className="card">
        <div className="list">
          {members.map((member, index) => (
            <div key={index} className="card card-tight card-flat" style={{ border: '1px solid var(--border)' }}>
              <div className="row">
                <input
                  className="input grow"
                  value={member.name}
                  onChange={(event) => update(index, { name: event.target.value })}
                  placeholder="Name"
                  aria-label={`Name of person ${index + 1}`}
                />
                {members.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-quiet"
                    aria-label="Remove person"
                    onClick={() => {
                      setMembers((current) => current.filter((_, i) => i !== index))
                      if (meIndex >= index && meIndex > 0) setMeIndex(meIndex - 1)
                    }}
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              <div className="chips">
                {(['parent', 'kid'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={`chip${member.role === role ? ' chip-on' : ''}`}
                    onClick={() => update(index, { role })}
                  >
                    {role === 'parent' ? 'Parent' : 'Kid'}
                  </button>
                ))}
                <button
                  type="button"
                  className={`chip${meIndex === index ? ' chip-on' : ''}`}
                  disabled={member.role !== 'parent'}
                  onClick={() => setMeIndex(index)}
                >
                  This is me
                </button>
              </div>

              <EmojiPicker value={member.emoji} onChange={(emoji) => update(index, { emoji })} />
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-block"
          onClick={() =>
            setMembers((current) => [
              ...current,
              { name: '', role: 'kid', emoji: STARTER_EMOJIS[current.length % STARTER_EMOJIS.length] ?? '🙂' },
            ])
          }
        >
          Add another person
        </button>
      </section>

      {named.length > 0 && !meIsParent ? (
        <p className="error-text">Mark which parent is you — that's the profile your login opens.</p>
      ) : null}
      <ErrorText error={create.error} />

      <button
        type="button"
        className="btn btn-primary btn-block btn-lg"
        disabled={!canSubmit}
        onClick={() => create.mutate()}
      >
        Create household
      </button>
      <button type="button" className="btn btn-quiet btn-block" onClick={() => setStep(1)}>
        Back
      </button>

      <p className="faint" style={{ textAlign: 'center' }}>
        Your chore lists and family agreement start from a sensible default — edit them to
        match how your house actually works.
      </p>
    </div>
  )
}
